<script>
import ModuleNavigationBar from '@/components/Modules/ModuleNavigationBar.vue';
import { mapGetters } from 'vuex';
import moment from 'moment/moment';
import InputRulesMixin from '@/Mixins/InputRulesMixin';
import AntInput from '@/components/AntInput.vue';
import { stringToConstantColor } from '@/components/Charts/utils/tasks-chart.utils';
import {
  generateAutomatedPlanning,
  generateManualPlanning,
  generateModulesPerDayPlanning,
} from '@/modules/daiwa-house-modular-europe/AssemblyPlanning/dhme-assembly-planning.utils';
import DhmeAssemblyPlanningChart from '@/modules/daiwa-house-modular-europe/AssemblyPlanning/DhmeAssemblyPlanningChart.vue';
import { DHME_PROJECT_ASSEMBLY_PLANNING } from '@/modules/modules';
import { getProject } from '@/services/api/projects.api';

export default {
  name: 'AssemblyPlanning',
  components: {
    DhmeAssemblyPlanningChart,
    AntInput,
    ModuleNavigationBar,
  },
  mixins: [InputRulesMixin],
  data: () => {
    return {
      selectedAssemblyHallId: null,
      selectedAssemblyConfiguration: null,
      selectedStartLocation: null,
      manualHallAssemblyGenerationMethod: 'normal',
      automatedTactPerDay: null,
      startDate: null,
      endDate: null,
      startHour: null,
      workHours: null,
      startDateMenu: false,
      endDateMenu: false,
      ganttScale: 3,
      ganttZoomLevels: [
        {
          key: 'year',
          value: 'Yearly',
        },
        {
          key: 'quarter',
          value: 'Quarter',
        },
        {
          key: 'month',
          value: 'Monthly',
        },
        {
          key: 'week',
          value: 'Weekly',
        },
        {
          key: 'day',
          value: 'Daily',
        },
        {
          key: 'hour',
          value: 'Hourly',
        },
      ],
      manualLocationTypeFilter: {},
      excludedLocations: [],
      dateTab: 'date',
      previousAlignedAssemblyPhase: null,
      generatingPlanning: false,
      locationPlanType: 'automated',
      modulesPerDay: null,
      rules: {
        canDivideHour: (value) =>
          60 % value === 0 || 'Needs to divide 60 evenly.',
      },
      totalLength: null,
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      project: 'project',
      requestedTasks: 'dhmeAssemblyPlanningStore/requestedAssemblyTasks',
      plannedTasks: 'dhmeAssemblyPlanningStore/plannedAssemblyTasks',
      focusedTask: 'dhmeAssemblyPlanningStore/focusedTask',
      focusedHall: 'dhmeAssemblyPlanningStore/focusedHall',
      assemblyHalls: 'dhmeAssemblyPlanningStore/assemblyHalls',
      assemblyConfigurations:
        'dhmeAssemblyPlanningStore/assemblyConfigurations',
      assemblyLocations: 'dhmeAssemblyPlanningStore/assemblyLocations',
      assemblyLanes: 'dhmeAssemblyPlanningStore/assemblyLanes',
      vacationDays: 'dhmeAssemblyPlanningStore/vacationCalendar',
      isLoading: 'dhmeAssemblyPlanningStore/isLoading',
      projectModules: 'dhmeAssemblyPlanningStore/projectModules',
      projectAssemblyTasks: 'dhmeAssemblyPlanningStore/projectAssemblyTasks',
      importCounter: 'dhmeAssemblyPlanningStore/importCounter',
      isDaiwaLicense: 'isDaiwaLicense',
    }),

    configurationsByHall() {
      return this.assemblyConfigurations.filter(
        (c) => c.assembly_hall === this.selectedAssemblyHallId
      );
    },
    sortedLocations() {
      const lanesMap = this.assemblyLanes.reduce((acc, lane) => {
        acc[lane.id] = lane.order;
        return acc;
      }, {});

      return [...this.assemblyLocations]
        .filter((l) => l.configuration === this.selectedAssemblyConfiguration)
        .sort((a, b) => {
          const laneOrderA = lanesMap[a.assembly_lane] || 0;
          const laneOrderB = lanesMap[b.assembly_lane] || 0;

          if (laneOrderA !== laneOrderB) {
            return laneOrderA - laneOrderB;
          }

          return (a.order || 0) - (b.order || 0);
        });
    },
    now() {
      return moment().format('YYYY-MM-DD');
    },
    moduleTypes() {
      return [...new Set(this.projectModules.map((m) => m.module_type))];
    },
    sortedModuleTypes() {
      return [...this.moduleTypes].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
    },
    selectedAssemblyHall() {
      return this.assemblyHalls.find(
        (h) => h.id === this.selectedAssemblyHallId
      );
    },
    configurationPhases() {
      return this.plannedTasks.filter(
        (t) =>
          t.task_type.custom_4 === this.selectedAssemblyConfiguration &&
          t.task_project.id !== this.focusedTask.task_project.id
      );
    },
    filteredRequestedTasks() {
      const q = (this.searchQuery || '').toLowerCase().trim();
      if (!q) return this.requestedTasks;
      return this.requestedTasks.filter((task) => {
        const num = String(task.task_project.number || '').toLowerCase();
        const name = (task.task_project.name || '').toLowerCase();
        const label = (task.task_type.custom_2 || '').toLowerCase();
        return num.includes(q) || name.includes(q) || label.includes(q);
      });
    },
  },
  watch: {
    selectedAssemblyHallId(newValue, oldValue) {
      if (newValue && oldValue && newValue !== oldValue) {
        this.selectedAssemblyConfiguration = null;
        this.startDate = null;
        this.startHour = null;
        this.workHours = null;
        this.startDateMenu = false;
        this.manualHallAssemblyGenerationMethod = 'normal';
        this.manualLocationTypeFilter = {};
        this.excludedLocations = [];
        this.locationPlanType = 'automated';
        this.modulesPerDay = null;
      }
    },
    focusedTask(value, oldValue) {
      if (value) {
        // Only reload planning UI state when switching to a different task.
        // If the same task object is replaced in the store (e.g. after
        // fetchModulesInOrder refreshes the task list), we must not reset
        // exclusions or filters the user has already configured.
        if (!oldValue || value.id !== oldValue.id) {
          this.startDate = value.planned_start
            ? moment(value.planned_start).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD');
          this.selectedAssemblyHallId = value.task_type.custom_3;
          this.selectedAssemblyConfiguration = value.task_type.custom_4;
          this.startHour = value.task_type.custom_5;
          this.workHours = value.task_type.custom_6;
          this.manualHallAssemblyGenerationMethod = value.task_type.custom_7;
          const metadata = JSON.parse(value.task_type.metadata);
          const rawFilter = metadata?.manualLocationTypeFilter ?? {};
          this.manualLocationTypeFilter = Object.fromEntries(
            Object.entries(rawFilter).map(([id, val]) => [
              id,
              Array.isArray(val) ? { mode: 'only', types: val } : val,
            ])
          );
          this.excludedLocations = metadata?.excludedLocations ?? [];
        }
        this.$store.dispatch('dhmeAssemblyPlanningStore/fetchModulesInOrder');
      }
    },
    ganttScale(value) {
      this.$refs['assembly-hall-gantt'].setZoomLevel(
        this.ganttZoomLevels[value].key
      );
    },
    project: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.$store.dispatch('dhmeAssemblyPlanningStore/fetchAssemblyTasks');
          this.$store.dispatch(
            'dhmeAssemblyPlanningStore/fetchAssemblyHallsAndConfigurations'
          );
        }
      },
    },
  },
  beforeDestroy() {
    this.closePlanner();
  },
  methods: {
    formatTimestamp(timestamp) {
      return moment(timestamp).format('DD:MM:YYYY HH:MM:ss');
    },
    focusOnTask(task) {
      if (task.id !== this.focusedTask?.id) {
        this.$store.commit('dhmeAssemblyPlanningStore/focus_on_task', task);
      } else {
        this.$store.commit('dhmeAssemblyPlanningStore/focus_on_task', null);
      }
    },
    closePlanner() {
      this.selectedAssemblyHallId = null;
      this.selectedAssemblyConfiguration = null;
      this.startDate = null;
      this.startHour = null;
      this.workHours = null;
      this.startDateMenu = false;
      this.manualHallAssemblyGenerationMethod = 'normal';
      this.manualLocationTypeFilter = {};
      this.excludedLocations = [];
      this.modulesPerDay = null;
      this.$refs?.assemblyPlanner?.reset();
      this.$store.commit('dhmeAssemblyPlanningStore/focus_on_task', null);
    },
    async planAssembly() {
      if (this.$refs.assemblyPlanner.validate()) {
        this.generatingPlanning = true;
        try {
          // Ensure the module + existing-task lists are current before we
          // generate. The focusedTask watcher fetches these asynchronously
          // without awaiting, so planning immediately after (re)opening could
          // run against a stale/empty task list and re-create tasks instead of
          // updating them (duplicate tasks). Awaiting here closes that race.
          await this.$store.dispatch(
            'dhmeAssemblyPlanningStore/fetchModulesInOrder'
          );

          const { automated: isAutomated } = this.selectedAssemblyHall;

          if (this.previousAlignedAssemblyPhase) {
            this.startHour =
              this.previousAlignedAssemblyPhase.task_type.custom_5;
            this.workHours =
              this.previousAlignedAssemblyPhase.task_type.custom_6;
          }

          if (this.projectModules.length === 0) {
            this.$store.commit('showNotification', {
              content:
                'Geen modules gevonden voor dit project. Planning kan niet worden aangemaakt.',
              color: 'warning',
            });
            return;
          }

          const modulesWithoutBuildNr = this.projectModules.filter(
            (m) => !m.build_nr
          );
          if (modulesWithoutBuildNr.length > 0) {
            this.$store.commit('showNotification', {
              content: `Planning geblokkeerd: ${modulesWithoutBuildNr.length} module(s) hebben geen build_nr. Controleer de moduledata.`,
              color: 'error',
            });
            return;
          }

          const modulesWithoutModuleId = this.projectModules.filter(
            (m) => !m.module_id
          );
          if (modulesWithoutModuleId.length > 0) {
            this.$store.commit('showNotification', {
              content: `Planning geblokkeerd: ${modulesWithoutModuleId.length} module(s) hebben geen module_id. Controleer de moduledata.`,
              color: 'error',
            });
            return;
          }

          const modulesWithoutType = this.projectModules.filter(
            (m) => !m.module_type
          );
          if (modulesWithoutType.length > 0) {
            this.$store.commit('showNotification', {
              content: `${modulesWithoutType.length} module(s) hebben geen module_type. Locatiefilters werken mogelijk niet correct.`,
              color: 'warning',
            });
          }

          let tasks = [];
          if (isAutomated) {
            tasks = await generateAutomatedPlanning(
              [...this.sortedLocations],
              parseInt(this.startHour),
              parseInt(this.workHours),
              this.startDate,
              this.automatedTactPerDay,
              this.projectModules,
              this.selectedAssemblyHallId,
              this.focusedTask,
              this.projectAssemblyTasks,
              this.vacationDays,
              this.previousAlignedAssemblyPhase ?? null
            );
          } else if (this.locationPlanType === 'manual' && this.modulesPerDay) {
            tasks = generateModulesPerDayPlanning(
              parseInt(this.modulesPerDay),
              parseInt(this.startHour),
              parseInt(this.workHours),
              this.startDate,
              this.projectModules,
              this.selectedAssemblyHallId,
              this.focusedTask,
              this.projectAssemblyTasks,
              this.vacationDays
            );
          } else {
            let locations = [...this.sortedLocations];
            if (this.selectedStartLocation) {
              let index = this.sortedLocations.findIndex(
                (x) => x.id === this.selectedStartLocation.id
              );
              locations = this.sortedLocations
                .slice(index)
                .concat(this.sortedLocations.slice(0, index));
            }

            locations = locations.filter(
              (l) => !this.excludedLocations.includes(l.id)
            );

            if (locations.length === 0) {
              this.$store.commit('showNotification', {
                content:
                  'Alle locaties zijn uitgesloten. Minimaal één locatie is vereist voor de planning.',
                color: 'warning',
              });
              return;
            }

            tasks = await generateManualPlanning(
              locations,
              parseInt(this.startHour),
              parseInt(this.workHours),
              this.startDate,
              this.manualHallAssemblyGenerationMethod,
              this.projectModules,
              this.manualLocationTypeFilter,
              this.focusedTask,
              this.projectAssemblyTasks,
              this.vacationDays,
              this.previousAlignedAssemblyPhase ?? null,
              this.locationPlanType
            );
          }

          if (tasks.length === 0) {
            this.$store.commit('showNotification', {
              content:
                'Planning aanmaken heeft geen taken opgeleverd. Controleer de module- en locatieconfiguratie.',
              color: 'warning',
            });
            return;
          }

          await this.$store.dispatch(
            'dhmeAssemblyPlanningStore/clearPreviousPlanning',
            { hallId: this.selectedAssemblyHallId }
          );

          this.totalLength = tasks.length;

          const endDate =
            this.locationPlanType === 'automated' || this.modulesPerDay
              ? moment
                  .max(tasks.map((t) => moment(t.planned_end)))
                  .format('YYYY-MM-DD HH:mm:ss')
              : moment(this.endDate)
                  .set({
                    hour: parseInt(this.startHour) + parseInt(this.workHours),
                    minute: 0,
                    second: 0,
                    millisecond: 0,
                  })
                  .format('YYYY-MM-DD HH:mm:ss');

          const obj = {
            id: this.focusedTask.id,
            planned_start: moment
              .min(tasks.map((t) => moment(t.planned_start)))
              .format('YYYY-MM-DD HH:mm:ss'),
            planned_end: endDate,
            due: endDate,
            options: {
              custom_1: 'planned',
              custom_3: this.selectedAssemblyHallId,
              custom_4: this.selectedAssemblyConfiguration,
              custom_5: this.startHour,
              custom_6: this.workHours,
              custom_7: this.manualHallAssemblyGenerationMethod,
              metadata: JSON.stringify({
                manualLocationTypeFilter: this.manualLocationTypeFilter,
                excludedLocations: this.excludedLocations,
              }),
            },
          };

          await this.$store.dispatch(
            'dhmeAssemblyPlanningStore/importAssemblyTasks',
            {
              tasks,
              assemblyPhase: obj,
            }
          );
          this.closePlanner();
        } catch (e) {
          this.$store.commit('showNotification', {
            content: `Planning mislukt: ${e.message}`,
            color: 'error',
          });
        } finally {
          this.generatingPlanning = false;
        }
      }
    },
    getLaneColor(location) {
      let l = this.sortedLocations.find(
        (item) => item.assembly_lane === location.assembly_lane
      );
      return stringToConstantColor(`${l.assembly_location}${l.id}`);
    },
    isLocationExcluded(locationId) {
      return this.excludedLocations.includes(locationId);
    },
    toggleLocationExclusion(locationId) {
      const idx = this.excludedLocations.indexOf(locationId);
      if (idx === -1) {
        this.excludedLocations.push(locationId);
      } else {
        this.excludedLocations.splice(idx, 1);
      }
    },
    getLocationMode(locationId) {
      const rule = this.manualLocationTypeFilter[locationId];
      if (!rule) return 'any';
      return rule.mode ?? 'any';
    },
    setLocationMode(locationId, mode) {
      const existing = this.manualLocationTypeFilter[locationId];
      const types = existing?.types ?? [];
      this.$set(this.manualLocationTypeFilter, locationId, { mode, types });
    },
    getLocationTypes(locationId) {
      return this.manualLocationTypeFilter[locationId]?.types ?? [];
    },
    setLocationTypes(locationId, types) {
      const mode = this.getLocationMode(locationId) || 'only';
      this.$set(this.manualLocationTypeFilter, locationId, {
        mode,
        types: types ?? [],
      });
    },
    allTypesSelected(locationId) {
      return (
        this.getLocationTypes(locationId).length === this.moduleTypes.length
      );
    },
    toggleAllTypes(locationId) {
      if (this.allTypesSelected(locationId)) {
        this.setLocationTypes(locationId, []);
      } else {
        this.setLocationTypes(locationId, [...this.moduleTypes]);
      }
    },
    async openProjectAssemblyPlanning() {
      const project = await getProject(this.focusedTask.task_project.id);
      const module = project.modules.find(
        (m) => m.route === DHME_PROJECT_ASSEMBLY_PLANNING
      );
      if (module) {
        await this.$store.dispatch('setProject', project);
        await this.$router.push({
          name: DHME_PROJECT_ASSEMBLY_PLANNING,
          params: {
            slug: project.slug,
          },
        });
      } else {
        this.$store.commit('showNotification', {
          content: 'Project assembly planning module not found',
          color: 'warning',
        });
      }
    },
    rejectPlanning() {
      this.$store.dispatch('dhmeAssemblyPlanningStore/rejectPlanning', {
        tasks: [
          {
            id: this.focusedTask.id,
            planned_start: null,
            planned_end: null,
            due: null,
            options: {
              custom_1: 'rejected',
              custom_3: null,
              custom_4: null,
              custom_5: null,
              custom_6: null,
              custom_7: null,
              metadata: null,
            },
          },
        ],
      });
      this.$store.commit('dhmeAssemblyPlanningStore/focus_on_task', null);
    },
    logSelected(event) {
      console.log(
        `[${event}] selectedAssemblyHallId:`,
        this.selectedAssemblyHallId
      );
    },
    isDateAllowed(date) {
      // Parse the date using moment
      const day = moment(date).day();
      // Allow weekdays only (Monday to Friday)
      return day !== 0 && day !== 6; // 0 = Sunday, 6 = Saturday
    },
  },
};
</script>

<template>
  <div class="d-flex flex-column">
    <module-navigation-bar title="Assembly planning">
      <template slot="module-nav-logo">
        <img
          v-if="isDaiwaLicense"
          alt=""
          height="40px"
          src="https://www.daiwahousemodular.eu/wp-content/uploads/daiwa_logo.svg"
        />
      </template>
    </module-navigation-bar>
    <v-progress-linear
      :style="{ opacity: isLoading ? 1 : 0 }"
      color="primary"
      height="5"
      indeterminate
    />
    <div class="flex-1 d-flex pa-5" style="height: 0">
      <div class="overflow-y-auto d-flex flex-column" style="width: 200px">
        <v-subheader class="justify-center">Assembly requests</v-subheader>
        <div
          v-if="filteredRequestedTasks.length > 0"
          class="flex-1 overflow-y-auto d-flex flex-column px-2 my-2"
        >
          <v-card
            v-for="task in filteredRequestedTasks"
            :key="task.id"
            :color="task.id === focusedTask?.id ? 'primary' : ''"
            :dark="task.id === focusedTask?.id"
            :disabled="generatingPlanning"
            class="mb-2"
            max-width="200"
            outlined
            @click="focusOnTask(task)"
          >
            <v-card-text class="d-flex align-center">
              <v-icon class="mr-2"> mdi-cube</v-icon>
              <div class="mr-2 d-flex flex-column">
                <span>{{ task.task_project.name }}</span>
                <v-chip
                  x-small
                  color="primary"
                  class="d-inline-flex align-self-start mt-2"
                  style="width: auto; min-width: 0; max-width: fit-content"
                >
                  {{ task.task_project.number }}
                </v-chip>

                <span class="font-italic fs-10">{{
                  task.task_type.custom_2
                }}</span>
                <span class="fs-12 mt-1">{{
                  formatTimestamp(task.created_at)
                }}</span>
              </div>
            </v-card-text>
          </v-card>
        </div>
        <div
          v-else
          class="font-italic d-flex align-center justify-center flex-1"
        >
          {{ requestedTasks.length === 0 ? 'No new requests' : 'No results' }}
        </div>
      </div>
      <v-card
        v-if="focusedTask"
        :disabled="generatingPlanning"
        class="background-white d-flex flex-column overflow-y-auto"
        outlined
        style="max-width: 500px"
      >
        <v-card-text class="d-flex flex-column">
          <div
            v-if="focusedTask.task_type.custom_1 === 'planned'"
            class="mb-5 d-flex align-center"
          >
            <v-chip>{{ focusedTask.task_project.name }}</v-chip>
            <v-spacer />
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  tile
                  v-bind="attrs"
                  @click="openProjectAssemblyPlanning"
                  v-on="on"
                >
                  <v-icon> mdi-arrow-expand-up</v-icon>
                </v-btn>
              </template>
              <span>Project assembly planning</span>
            </v-tooltip>
          </div>
          <v-form ref="assemblyPlanner" class="d-flex flex-column">
            <div class="d-flex align-center">
              <ant-input class="mr-5" label="Assembly Hall" top-margin="mt-0">
                <template #input-field>
                  <v-select
                    v-model="selectedAssemblyHallId"
                    :items="assemblyHalls"
                    dense
                    filled
                    item-text="title"
                    item-value="id"
                    placeholder="hall"
                    @change="logSelected('change')"
                    @input="logSelected('input')"
                    @update:modelValue="logSelected('update:modelValue')"
                  >
                    <template #item="{ item }">
                      {{ item.title }}
                      <v-spacer />
                      <v-chip class="mx-2" small>
                        <v-icon left small
                          >{{
                            item.automated
                              ? 'mdi-robot-industrial'
                              : 'mdi-account-wrench'
                          }}
                        </v-icon>
                        {{ item.automated ? 'Automated' : 'Manual' }}
                      </v-chip>
                    </template>
                  </v-select>
                </template>
              </ant-input>

              <ant-input label="Configuration" top-margin="mt-0">
                <template #input-field>
                  <v-select
                    v-model="selectedAssemblyConfiguration"
                    :disabled="!selectedAssemblyHallId"
                    :items="configurationsByHall"
                    :rules="[rules.required]"
                    dense
                    filled
                    item-text="title"
                    item-value="id"
                    placeholder="Configuration"
                  />
                </template>
              </ant-input>
            </div>
            <v-btn-toggle
              v-if="
                !selectedAssemblyHall?.automated &&
                selectedAssemblyHallId &&
                selectedAssemblyConfiguration
              "
              v-model="locationPlanType"
              color="primary"
              group
              mandatory
              tile
            >
              <v-btn class="flex-1" value="automated"> Automated</v-btn>

              <v-btn class="flex-1" value="manual"> Manual</v-btn>
            </v-btn-toggle>
            <v-subheader
              v-if="
                selectedAssemblyHallId &&
                selectedAssemblyConfiguration &&
                !selectedAssemblyHall?.automated
              "
              class="mb-5"
            >
              {{
                locationPlanType === 'automated'
                  ? 'Locations are determined by the system in the most optimal way'
                  : 'Locations are manually defined at moment of assembly'
              }}
            </v-subheader>
            <div
              v-if="selectedAssemblyHallId && selectedAssemblyConfiguration"
              class="d-flex flex-column"
            >
              <div v-if="locationPlanType === 'automated'" class="d-flex">
                <v-btn-toggle
                  v-model="dateTab"
                  color="primary"
                  group
                  mandatory
                  tile
                >
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn small v-bind="attrs" value="date" v-on="on">
                        Date
                      </v-btn>
                    </template>
                    <span
                      >Pick a date and time when the assembly will start in the
                      hall</span
                    >
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn small v-bind="attrs" value="project" v-on="on">
                        Project
                      </v-btn>
                    </template>
                    <span
                      >Select the project where the current planning will align
                      with at the end</span
                    >
                  </v-tooltip>
                </v-btn-toggle>
              </div>

              <div v-if="dateTab === 'date'">
                <div class="d-flex">
                  <ant-input class="flex-grow-1" label="Start current">
                    <template #input-field>
                      <v-menu
                        v-model="startDateMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        min-width="auto"
                        offset-y
                        transition="scale-transition"
                      >
                        <template #activator="{ on, attrs }">
                          <v-text-field
                            v-model="startDate"
                            :rules="[rules.required]"
                            dense
                            filled
                            hide-details
                            placeholder="2024-01-01"
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <v-date-picker
                          v-model="startDate"
                          :min="now"
                          :allowed-dates="isDateAllowed"
                          @input="startDateMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </template>
                  </ant-input>
                  <ant-input
                    v-if="locationPlanType === 'manual' && !modulesPerDay"
                    class="flex-grow-1 ml-5"
                    label="Expected End"
                  >
                    <template #input-field>
                      <v-menu
                        v-model="endDateMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        min-width="auto"
                        offset-y
                        transition="scale-transition"
                      >
                        <template #activator="{ on, attrs }">
                          <v-text-field
                            v-model="endDate"
                            :rules="[rules.required]"
                            dense
                            filled
                            hide-details
                            placeholder="2024-01-01"
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <v-date-picker
                          v-model="endDate"
                          :min="startDate"
                          @input="endDateMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </template>
                  </ant-input>
                </div>
                <div class="d-flex">
                  <ant-input class="mr-5 flex-grow-1" label="Start hour">
                    <template #input-field>
                      <v-text-field
                        v-model="startHour"
                        :rules="[rules.required]"
                        dense
                        filled
                        hide-details
                        placeholder="7"
                        type="number"
                      />
                    </template>
                  </ant-input>
                  <ant-input class="flex-grow-1" label="Work hours">
                    <template #input-field>
                      <v-text-field
                        v-model="workHours"
                        :rules="[rules.required]"
                        dense
                        filled
                        hide-details
                        placeholder="8"
                        type="number"
                      />
                    </template>
                  </ant-input>
                </div>
                <ant-input
                  v-if="
                    !selectedAssemblyHall?.automated &&
                    locationPlanType === 'manual'
                  "
                  hint="Number of modules to assemble per working day, in assembly_sequence order"
                  is-optional
                  label="Modules per day"
                >
                  <template #input-field>
                    <v-text-field
                      v-model="modulesPerDay"
                      clearable
                      dense
                      filled
                      hide-details
                      min="1"
                      placeholder="6"
                      type="number"
                    />
                  </template>
                </ant-input>
              </div>
              <div v-if="dateTab === 'project'">
                <ant-input label="Project">
                  <template #input-field>
                    <v-select
                      v-model="previousAlignedAssemblyPhase"
                      :items="configurationPhases"
                      :rules="[rules.required]"
                      dense
                      filled
                      hide-details
                      item-text="task_project.name"
                      placeholder="project"
                      return-object
                    />
                  </template>
                </ant-input>
              </div>
              <div
                v-if="
                  !selectedAssemblyHall?.automated &&
                  locationPlanType === 'automated'
                "
                class="d-flex flex-column"
              >
                <ant-input label="Method">
                  <template #input-field>
                    <v-select
                      v-model="manualHallAssemblyGenerationMethod"
                      :items="['normal', 'pendulum', 'waterfall']"
                      :rules="[rules.required]"
                      dense
                      filled
                      placeholder="normal"
                    />
                  </template>
                </ant-input>

                <div
                  v-if="locationPlanType === 'automated'"
                  class="d-flex flex-column"
                >
                  <div class="caption text--secondary px-2 pb-2">
                    <strong>Any</strong> = no restriction &nbsp;&middot;&nbsp;
                    <strong>Only</strong> = whitelist &nbsp;&middot;&nbsp;
                    <strong>Exclude</strong> = blacklist
                  </div>
                  <div
                    class="overflow-y-auto py-2"
                    style="min-height: 240px; max-height: 480px"
                  >
                    <div
                      v-for="location in sortedLocations"
                      :key="location.id"
                      :class="
                        manualHallAssemblyGenerationMethod === 'pendulum' &&
                        parseInt(location.assembly_lane) % 2 === 0
                          ? 'flex-row-reverse'
                          : 'flex-row'
                      "
                      class="d-flex align-center px-2 py-1 mb-2"
                      :style="{
                        'min-height': '50px',
                        opacity: isLocationExcluded(location.id) ? 0.4 : 1,
                      }"
                    >
                      <div
                        :style="{ 'background-color': getLaneColor(location) }"
                        class="lane-color-block mx-2"
                      />
                      {{ location.assembly_location }}
                      <v-spacer />
                      <v-btn-toggle
                        :value="getLocationMode(location.id)"
                        class="mx-3"
                        dense
                        mandatory
                        @change="setLocationMode(location.id, $event)"
                      >
                        <v-btn small value="any">Any</v-btn>
                        <v-btn small value="only">Only</v-btn>
                        <v-btn small value="exclude">Exclude</v-btn>
                      </v-btn-toggle>
                      <v-autocomplete
                        v-if="getLocationMode(location.id) !== 'any'"
                        :items="sortedModuleTypes"
                        :placeholder="
                          getLocationMode(location.id) === 'only'
                            ? 'Allowed types'
                            : 'Blocked types'
                        "
                        :value="getLocationTypes(location.id)"
                        chips
                        class="mr-3"
                        clearable
                        dense
                        filled
                        hide-details
                        hide-no-data
                        multiple
                        small-chips
                        style="max-width: 300px"
                        @input="setLocationTypes(location.id, $event)"
                      >
                        <template #prepend-item>
                          <v-list-item
                            ripple
                            @mousedown.prevent
                            @click="toggleAllTypes(location.id)"
                          >
                            <v-list-item-action>
                              <v-icon
                                :color="
                                  allTypesSelected(location.id) ? 'primary' : ''
                                "
                              >
                                {{
                                  allTypesSelected(location.id)
                                    ? 'mdi-checkbox-marked'
                                    : 'mdi-checkbox-blank-outline'
                                }}
                              </v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>Select All</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider class="mt-2" />
                        </template>
                        <template #selection="{ item, index }">
                          <v-chip v-if="index === 0" small>
                            <span
                              v-if="getLocationTypes(location.id).length > 1"
                            >
                              {{ getLocationTypes(location.id).length }} types
                            </span>
                            <span v-else>{{ item }}</span>
                          </v-chip>
                        </template>
                      </v-autocomplete>
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn
                            :color="
                              isLocationExcluded(location.id) ? 'error' : ''
                            "
                            icon
                            small
                            v-on="on"
                            @click="toggleLocationExclusion(location.id)"
                          >
                            <v-icon small>
                              {{
                                isLocationExcluded(location.id)
                                  ? 'mdi-cancel'
                                  : 'mdi-check-circle-outline'
                              }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{
                          isLocationExcluded(location.id)
                            ? 'Excluded from planning'
                            : 'Exclude from planning'
                        }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="selectedAssemblyHall?.automated"
                class="d-flex flex-column"
              >
                <ant-input label="Tacts per day">
                  <template #input-field>
                    <v-text-field
                      v-model="automatedTactPerDay"
                      :rules="[rules.required]"
                      dense
                      filled
                      placeholder="10"
                      type="number"
                    />
                  </template>
                </ant-input>
              </div>
              <div
                v-if="
                  !selectedAssemblyHall?.automated &&
                  locationPlanType === 'automated'
                "
                class="d-flex flex-column"
              >
                <ant-input
                  hint="Set the location from where the assembly planning is started (default is the first location)"
                  is-optional
                  label="Start on location"
                >
                  <template #input-field>
                    <v-select
                      v-model="selectedStartLocation"
                      :disabled="!selectedAssemblyConfiguration"
                      :items="sortedLocations"
                      dense
                      filled
                      item-text="assembly_location"
                      placeholder="Location"
                      return-object
                    >
                      <template #item="{ item }">
                        {{ item.assembly_location }}
                        <v-spacer />
                        <span class="fs-10 font-italic">{{ item.order }}</span>
                      </template>
                    </v-select>
                  </template>
                </ant-input>
              </div>
            </div>
            <div
              v-if="selectedAssemblyHallId && selectedAssemblyConfiguration"
              class="d-flex align-center mt-5"
            >
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <div class="mr-5" v-bind="attrs" v-on="on">
                    <v-icon class="mr-2">mdi-account-wrench</v-icon>
                    {{ sortedLocations.length }}
                  </div>
                </template>
                <span>Locations</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <div class="mr-5" v-bind="attrs" v-on="on">
                    <v-icon class="mr-2">mdi-cube</v-icon>
                    {{ projectModules.length }}
                  </div>
                </template>
                <span>Modules</span>
              </v-tooltip>
              <span v-if="generatingPlanning">
                {{ importCounter }}/{{ totalLength }}
              </span>
              <v-spacer />
              <v-btn
                :disabled="generatingPlanning"
                class="mr-5"
                color="error"
                text
                @click="closePlanner"
                >Cancel
              </v-btn>
              <v-btn
                :disabled="generatingPlanning"
                :loading="generatingPlanning"
                color="primary"
                elevation="0"
                @click="planAssembly"
                >Plan
              </v-btn>
            </div>
            <v-btn
              v-if="focusedTask.task_type.custom_1 === 'planned'"
              class="mt-5"
              color="warning"
              elevation="0"
              small
              @click="rejectPlanning"
              >Reject planning
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>

      <div class="flex-1 d-flex flex-column ml-5" style="width: 0">
        <div class="d-flex mb-5 align-center px-2">
          <v-chip
            v-if="focusedHall"
            close
            @click:close="
              $store.commit('dhmeAssemblyPlanningStore/focus_on_hall', null)
            "
          >
            <v-icon
              >{{
                focusedHall.automated
                  ? 'mdi-robot-industrial'
                  : 'mdi-account-wrench'
              }}
            </v-icon>
            {{ focusedHall.title }}
          </v-chip>
          <v-text-field
            v-model="searchQuery"
            clearable
            dense
            filled
            hide-details
            placeholder="Search project, number..."
            prepend-inner-icon="mdi-magnify"
            style="max-width: 260px"
            class="mr-3"
          />
          <v-spacer />
          <v-btn-toggle
            v-model="ganttScale"
            color="primary"
            dense
            mandatory
            rounded
          >
            <v-btn v-for="option in ganttZoomLevels" :key="option.key">
              {{ option.value }}
            </v-btn>
          </v-btn-toggle>
        </div>
        <dhme-assembly-planning-chart
          ref="assembly-hall-gantt"
          :filter-query="searchQuery"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.lane-color-block {
  width: 5px;
  height: 100%;
  border-radius: 15px;
}
</style>
